import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CONSENT_NOTICE } from "./SignUp";

/**
 * Resident self-join via a code — the "residents join easily" path.
 *
 * Two steps on purpose: confirm which estate the code belongs to *before* asking for a
 * password. A code is something handed round a WhatsApp group, so the person redeeming it
 * should see what they are joining before they commit anything.
 */
export default function JoinEstate() {
    const navigate = useNavigate();
    const { code: codeFromUrl } = useParams();
    const { joinWithCode } = useAuth();

    const [code, setCode] = useState(codeFromUrl || "");
    const [estate, setEstate] = useState(null);
    const [checking, setChecking] = useState(false);
    const [form, setForm] = useState({ full_name: "", email: "", password: "" });
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const check = async (value) => {
        const c = (value ?? code).trim();
        if (!c) return;
        setChecking(true);
        try {
            const res = await api.get(`/onboarding/join/${encodeURIComponent(c)}`);
            setEstate(res.data);
        } catch (err) {
            setEstate(null);
            toast.error(err?.response?.data?.detail || "That join code isn't valid");
        } finally {
            setChecking(false);
        }
    };

    useEffect(() => {
        if (codeFromUrl) check(codeFromUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codeFromUrl]);

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setSubmitting(true);
        try {
            await joinWithCode({ ...form, code: code.trim() });
            toast.success(`You've joined ${estate.estate_name}`);
            navigate("/");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not join this estate");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#F9FAFB]">
            <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8" data-testid="join-panel">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                    <Users size={14} /> Join your estate
                </div>

                {!estate ? (
                    <>
                        <h1 className="font-heading text-2xl font-bold text-slate-900 mt-2">
                            Enter your join code
                        </h1>
                        <p className="text-sm text-slate-600 mt-2">
                            Your estate's directors will have shared a code with you.
                        </p>
                        <div className="mt-6 space-y-3">
                            <Label htmlFor="code">Join code</Label>
                            <Input
                                id="code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && check()}
                                placeholder="e.g. D3JT7oFrzeHu"
                                data-testid="join-code-input"
                            />
                            <Button
                                onClick={() => check()}
                                disabled={checking || !code.trim()}
                                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                                data-testid="join-code-check-button"
                            >
                                {checking ? "Checking…" : "Continue"}
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <h1 className="font-heading text-2xl font-bold text-slate-900 mt-2">
                            Join {estate.estate_name}
                        </h1>
                        <p className="text-sm text-slate-600 mt-2">
                            Create your account to see your estate's shared documents and raise issues.
                        </p>

                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="j_name">Your name</Label>
                                <Input id="j_name" value={form.full_name} onChange={set("full_name")} required data-testid="join-name-input" />
                            </div>
                            <div>
                                <Label htmlFor="j_email">Email</Label>
                                <Input id="j_email" type="email" value={form.email} onChange={set("email")} required data-testid="join-email-input" />
                            </div>
                            <div>
                                <Label htmlFor="j_password">Password</Label>
                                <Input
                                    id="j_password"
                                    type="password"
                                    value={form.password}
                                    onChange={set("password")}
                                    required
                                    minLength={8}
                                    placeholder="At least 8 characters"
                                    data-testid="join-password-input"
                                />
                            </div>

                            <div className="rounded-md bg-slate-50 border border-slate-200 p-3.5 flex gap-2.5">
                                <Lock size={15} className="text-slate-500 shrink-0 mt-0.5" />
                                <p className="text-xs text-slate-600 leading-relaxed">
                                    Anything you upload stays <strong>private to you</strong> unless you
                                    choose to share it. Your neighbours can't see your documents, and you
                                    can't see theirs.
                                </p>
                            </div>

                            <Button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                                data-testid="join-submit-button"
                            >
                                {submitting ? "Joining…" : `Join ${estate.estate_name}`}
                                {!submitting && <ArrowRight size={16} className="ml-1.5" />}
                            </Button>
                            <p className="text-xs text-slate-500 text-center" data-testid="join-consent-notice">
                                {CONSENT_NOTICE}
                            </p>
                            <button
                                type="button"
                                onClick={() => setEstate(null)}
                                className="w-full text-xs text-slate-500 hover:text-slate-700 underline"
                            >
                                Use a different code
                            </button>
                        </form>
                    </>
                )}

                <div className="mt-6 pt-6 border-t border-slate-200 text-sm text-slate-600">
                    No code?{" "}
                    <Link to="/signup" className="text-slate-900 font-medium underline">
                        Start your own private workspace
                    </Link>
                </div>
            </div>
        </div>
    );
}
